// Stolen from @splunk/react-docs because it is not exported
import React, { useContext, useCallback, useMemo } from 'react';
import { variables, useSplunkTheme } from '@splunk/themes';
import { AnimationToggleContext } from '@splunk/react-ui/AnimationToggle';
import RadioList, {
    type RadioListChangeHandler,
} from '@splunk/react-ui/RadioList';
import Heading from '@splunk/react-ui/Heading';
import styled from 'styled-components';
import Button from '@splunk/react-ui/Button';
import Dropdown from '@splunk/react-ui/Dropdown';
import { _ } from '@splunk/ui-utils/i18n';
import { deprecated } from '@splunk/dashboard-utils';

const deprecationMessage = _(
    'ThemeMenu is intended for testing purposes only and is deprecated. It will be removed in the next major version.'
);

const StyledSectionWrapper = styled.div`
    padding: 12px 20px;
    width: 220px;

    & + & {
        border-top: 1px solid ${variables.borderColor};
    }
`;

const StyledButton = styled(Button)`
    text-transform: capitalize;
`;

type Setter = (value: string) => void;

interface ThemeMenuProps {
    onToggleAnimation?: (value: boolean) => void;
    onChangeThemeFamily?: Setter;
    onChangeColorScheme?: Setter;
    onChangeDensity?: Setter;
}

/**
 * @deprecated This component is intended for testing only and will be removed
 * in the next major version.
 */
export const ThemeMenu = ({
    onToggleAnimation,
    onChangeThemeFamily,
    onChangeColorScheme,
    onChangeDensity,
}: ThemeMenuProps) => {
    deprecated(deprecationMessage);
    const animationToggleContextEnabled = useContext(AnimationToggleContext);
    const { colorScheme, density, family, backgroundColorNavigation } =
        useSplunkTheme();

    const themeFamily =
        family === 'prisma' &&
        backgroundColorNavigation.startsWith('linear-gradient')
            ? 'splunk-magnetic'
            : family;

    const handleFamilyChange = useCallback<RadioListChangeHandler>(
        (_e, { value }) => onChangeThemeFamily?.(value as string),
        [onChangeThemeFamily]
    );

    const handleColorSchemeChange = useCallback<RadioListChangeHandler>(
        (_e, { value }) => onChangeColorScheme?.(value as string),
        [onChangeColorScheme]
    );

    const handleDensityChange = useCallback<RadioListChangeHandler>(
        (_e, { value }) => onChangeDensity?.(value as string),
        [onChangeDensity]
    );

    const handleAnimationChange = useCallback<RadioListChangeHandler>(
        (_e, { value }) => onToggleAnimation?.(value as boolean),
        [onToggleAnimation]
    );

    const Toggle = useMemo(
        () => (
            <StyledButton
                isMenu
                label={`${themeFamily} ${colorScheme} ${density}`}
            />
        ),
        [themeFamily, colorScheme, density]
    );

    return (
        <Dropdown toggle={Toggle}>
            {onChangeThemeFamily && (
                <StyledSectionWrapper>
                    <Heading level={6}>{_('Theme family')}</Heading>
                    <RadioList
                        onChange={handleFamilyChange}
                        value={themeFamily}
                    >
                        <RadioList.Option value="enterprise">
                            {_('Enterprise')}
                        </RadioList.Option>
                        <RadioList.Option value="prisma">
                            {_('Prisma')}
                        </RadioList.Option>
                        <RadioList.Option value="splunk-magnetic">
                            {_('Splunk Magnetic')}
                        </RadioList.Option>
                    </RadioList>
                </StyledSectionWrapper>
            )}
            {onChangeColorScheme && (
                <StyledSectionWrapper>
                    <Heading level={6}>{_('Color scheme')}</Heading>
                    <RadioList
                        onChange={handleColorSchemeChange}
                        value={colorScheme}
                    >
                        <RadioList.Option value="light">
                            {_('Light')}
                        </RadioList.Option>
                        <RadioList.Option value="dark">
                            {_('Dark')}
                        </RadioList.Option>
                    </RadioList>
                </StyledSectionWrapper>
            )}
            {onChangeDensity && (
                <StyledSectionWrapper>
                    <Heading level={6}>{_('Density')}</Heading>
                    <RadioList onChange={handleDensityChange} value={density}>
                        <RadioList.Option value="comfortable">
                            {_('Comfortable')}
                        </RadioList.Option>
                        <RadioList.Option value="compact">
                            {_('Compact')}
                        </RadioList.Option>
                    </RadioList>
                </StyledSectionWrapper>
            )}
            {onToggleAnimation && (
                <StyledSectionWrapper>
                    <Heading level={6}>{_('Animation')}</Heading>
                    <RadioList
                        onChange={handleAnimationChange}
                        value={animationToggleContextEnabled}
                    >
                        <RadioList.Option value>
                            {_('Enabled')}
                        </RadioList.Option>
                        <RadioList.Option value={false}>
                            {_('Disabled')}
                        </RadioList.Option>
                    </RadioList>
                </StyledSectionWrapper>
            )}
        </Dropdown>
    );
};
